// This is the React component for Section 9 ("The Growth")
// It uses GSAP's matchMedia for two layouts:
// 1. Desktop: A pinned, cinematic reveal.
// 2. Mobile/Tablet: A stacked, waterfall reveal.

import React, { useLayoutEffect, useRef } from "react";
import "./Section9.css"; // We will create this file next
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Import the images for the 4 growth items
import dPlantImg from "../../../assets/images/Dplant.png";
import dGrowImg from "../../../assets/images/Dgrow.png";
import dSeedImg from "../../../assets/images/Dseed.png";
import dFlyImg from "../../../assets/images/Dfly.png";

gsap.registerPlugin(ScrollTrigger);

// --- Component Text & Data ---
// An array of objects for the 4 growth steps.
const growthSteps = [
  {
    title: "PLANT",
    subtitle: "Start Strong",
    text: "Root your program in fun and learning by doing.",
    image: dPlantImg,
    // This class will be used for desktop positioning
    cssClass: "item-1",
  },
  {
    title: "GROW",
    subtitle: "Connection",
    text: "Build confidence through consistency",
    image: dGrowImg,
    cssClass: "item-2",
  },
  {
    title: "SEED",
    subtitle: "Cultivation",
    text: "Encourage independence and leadership.",
    image: dSeedImg,
    cssClass: "item-3",
  },
  {
    title: "FLY",
    subtitle: "Grow With Trust",
    text: "Resilience takes flight, carried by trust and newfound confidence and seeds of possibility",
    image: dFlyImg,
    cssClass: "item-4",
  },
];

const mainTitle = "The Growth of a Dandelion Runner";

// --- React Component ---
function Section9() {
  const sectionRef = useRef(null);
  const sunRef = useRef(null);
  // --- REFS UPDATED ---
  const titleContainerRef = useRef(null); // Ref for the title's container
  //const dandelionContainerRef = useRef(null); // Ref for the dandelions' container
  const desktopItemRefs = useRef([]);
  // Mobile refs are unchanged
  const mobileItemRefs = useRef([]);
  const mobileTitleRef = useRef(null);

  useLayoutEffect(() => {
    // This correctly prepares the ref arrays without deleting them.
    desktopItemRefs.current = desktopItemRefs.current.slice(
      0,
      growthSteps.length
    );
    mobileItemRefs.current = mobileItemRefs.current.slice(
      0,
      growthSteps.length
    );

    const ctx = gsap.context(() => {
      ScrollTrigger.matchMedia({
        // --- 1. DESKTOP ANIMATION (Pinned, Cinematic) ---
        "(min-width: 1024px)": function () {
          const items = desktopItemRefs.current;

          const tl = gsap.timeline({
            scrollTrigger: {
              trigger: sectionRef.current,
              pin: true,
              start: "top top",
              end: "+=4500", // Long pin duration
              scrub: 1, // Link to scrollbar
              toggleActions: "play reverse play reverse",

              // Smooth finish
              onLeave: (self) => {
                gsap.to(self.animation, {
                  progress: 1,
                  duration: 0.5,
                  ease: "power1.out",
                });
                self.disable();
                self.pin && self.pin.style.removeProperty("transform");
                ScrollTrigger.refresh();
              },
            },
          });

          // Animation 1: Sun fades in
          tl.fromTo(sunRef.current, { opacity: 0 }, { opacity: 1, duration: 1 }, 0); // At 0 seconds

          // Animation 2: Main title fades in
          tl.fromTo(
            titleContainerRef.current, // Animate the title container
            { opacity: 0, y: 30 },
            { opacity: 1, y: 0, duration: 1 },
            0.5 // At 0.5 seconds
          );

          // Animation 3: Items "grow from the ground"
          tl.fromTo(
            items,
            { opacity: 0, y: 400 }, // Start hidden and 400px down
            {
              opacity: 1,
              y: 0, // End in their final Figma position
              duration: 1.5,
              stagger: 0.25, // Stagger them in
              ease: "power2.out",
            },
            1.5 // Start this sequence at 1.5s
          );
        },

        // --- 2. TABLET & MOBILE ANIMATION (Waterfall) ---
        "(max-width: 1023px)": function () {
          // ... (THIS PART IS UNCHANGED) ...
          const mobileElements = [
            mobileTitleRef.current,
            ...mobileItemRefs.current,
          ];

          gsap.fromTo(
            mobileElements,
            { opacity: 0, y: 50 },
            {
              opacity: 1,
              y: 0,
              duration: 0.8,
              stagger: 0.2,
              ease: "power2.out",
              scrollTrigger: {
                trigger: sectionRef.current,
                start: "top 80%",
                toggleActions: "play none none none",
                once: true,
              },
            }
          );
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  // Helper functions to add refs to the arrays
  const addDesktopRef = (el) => {
    if (el && !desktopItemRefs.current.includes(el)) {
      desktopItemRefs.current.push(el);
    }
  };
  const addMobileRef = (el) => {
    if (el && !mobileItemRefs.current.includes(el)) {
      mobileItemRefs.current.push(el);
    }
  };

  // --- JSX (HTML) ---
  return (
    <section className="section-9" ref={sectionRef}>
      
      {/* Scenery: Sun (for desktop) */}
      <div className="section-9-sun" ref={sunRef}></div>
      {/* Scenery: Sun (for mobile/tablet) */}
      <div className="section-9-sun-mobile"></div>

      {/* --- FIX: NEW DESKTOP STRUCTURE --- */}

      {/* Container 1: Title (z-index 11) */}
      <div className="section-9-title-container" ref={titleContainerRef}>
        <h2 className="section-9-main-title">{mainTitle}</h2>
      </div>

      {/* Container 2: Dandelions (z-index 8) */}
      <div className="section-9-dandelion-container">
        {growthSteps.map((item, index) => (
          <div
            className={`section-9-item-desktop ${item.cssClass}`}
            key={index}
            ref={addDesktopRef}
          >
            <h3 className="section-9-item-title">{item.title}</h3>
            <span className="section-9-item-subtitle">{item.subtitle}</span>
            <p className="section-9-item-text">{item.text}</p>
            <img
              src={item.image}
              alt={item.title}
              className="section-9-item-img"
            />
          </div>
        ))}
      </div>
      
      {/* Scenery: Mountains (z-index 10) */}
      <div className="section-9-mountains"></div>

      {/* --- Mobile/Tablet Layout (Stacked) --- */}
      {/* This part is unchanged */}
      <div className="section-9-mobile-container">
        <h2 className="section-9-main-title" ref={mobileTitleRef}>
          {mainTitle}
        </h2>

        {growthSteps.map((item, index) => (
          <div
            className="section-9-item-mobile"
            key={index}
            ref={addMobileRef}
          >
            <img
              src={item.image}
              alt={item.title}
              className="section-9-item-img"
            />
            <h3 className="section-9-item-title">{item.title}</h3>
            <span className="section-9-item-subtitle">{item.subtitle}</span>
            <p className="section-9-item-text">{item.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export default Section9;